import React, { useState, useRef, useEffect } from 'react';
import { X, Send, GraduationCap, User, HelpCircle, BookOpen, Clock, CheckCircle, Award, Users } from 'lucide-react';

const AITeacherChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // 👉 Kết nối WebSocket khi component mount
  useEffect(() => {
    const ws = new WebSocket("wss://cmssba.metatekvn.com/ws/chat"); 
    socketRef.current = ws;

    ws.onopen = () => {
      console.log("✅ Connected to WebSocket server");
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      // console.log("📩 Server:", data);

      if (data.error) {
        setMessages(prev => [...prev, {
          id: Date.now(),
          text: "⚠️ " + data.error,
          sender: 'ai',
          timestamp: new Date(),
          type: 'error'
        }]);
      } else if (data.answer || data.response) {
        // Phản hồi AI
        setMessages(prev => [...prev, {
          id: Date.now(),
          text: data.answer || data.response || JSON.stringify(data),
          sender: 'ai',
          timestamp: new Date(),
          type: 'response'
        }]);
      }
      setIsTyping(false);
    };

    ws.onclose = () => {
      console.log("🔌 Disconnected from WebSocket server");
    };

    return () => {
      ws.close();
    };
  }, []);

  const handleSendMessage = () => {
    if (!inputText.trim()) return;
    const userMessage = {
      id: messages.length + 1,
      text: inputText,
      sender: 'user',
      timestamp: new Date(),
      type: 'question'
    };

    setMessages(prev => [...prev, userMessage]);
    setIsTyping(true);

    // 👉 gửi lên server
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        message: inputText,
        platform: { src: "web", usr_id: "frontend-user" },
        message_id: Date.now().toString()
      }));
    }

    setInputText('');
  };

  return (
    <>
      {/* Fixed Floating Button - Always visible */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-105 transition-all duration-300 relative group"
        >
          <GraduationCap size={28} />
          
          {/* Tooltip - only shows on hover when closed */}
          {!isOpen && (
            <div className="absolute right-full mr-3 bg-gray-800 text-white px-4 py-2 rounded-lg text-sm whitespace-nowrap font-medium opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
              👩‍🏫 AI Teacher
            </div>
          )}
          
          {/* Notification badge */}
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center">
            <span className="text-xs text-white font-bold">💡</span>
          </div>
        </button>
      </div>

      {/* Chat Widget - Positioned separately */}
      {isOpen && (
        <div className="fixed bottom-6 right-24 z-40 bg-white rounded-2xl shadow-2xl border border-gray-200 w-96 h-[600px] flex flex-col animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-2xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center border-2 border-white/30">
                <GraduationCap size={22} />
              </div>
              <div>
                <h3 className="font-semibold text-lg">AI Teacher</h3>
                <p className="text-xs opacity-90">🎓 Virtual Teaching Assistant</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="hover:bg-white/20 p-2 rounded-full transition-colors"
            >
              <X size={20} />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-white">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex gap-3 max-w-[85%] ${message.sender === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    message.sender === 'user' 
                      ? 'bg-blue-600 text-white border-2 border-blue-200' 
                      : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white border-2 border-blue-200'
                  }`}>
                    {message.sender === 'user' ? <User size={16} /> : <GraduationCap size={18} />}
                  </div>
                  <div className={`rounded-2xl px-4 py-3 shadow-sm ${
                    message.sender === 'user'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-white text-gray-800 rounded-bl-sm border border-gray-100'
                  }`}>
                    <div className="text-sm whitespace-pre-line leading-relaxed">{message.text}</div>
                    <div className={`text-xs mt-2 ${
                      message.sender === 'user' ? 'text-blue-100' : 'text-gray-400'
                    }`}>
                      {message.sender === 'ai' ? '👩‍🏫 ' : ''}
                      {message.timestamp.toLocaleTimeString('en-US', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            
            {isTyping && (
              <div className="flex justify-start">
                <div className="flex gap-3 max-w-[85%]">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white flex items-center justify-center border-2 border-blue-200">
                    <GraduationCap size={18} />
                  </div>
                  <div className="bg-white rounded-2xl rounded-bl-sm px-4 py-3 border border-gray-100 shadow-sm">
                    <div className="flex items-center gap-2 text-sm text-gray-500 mb-1">
                      <span>👩‍🏫 Teacher is thinking...</span>
                    </div>
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                      <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t bg-white rounded-b-2xl">
            <div className="text-xs text-gray-500 mb-2 text-center">💬 Ask me anything about your exam</div>
            <div className="flex gap-2">
              <input
                type="text"
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="I have a question about..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-full focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 text-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputText.trim() || isTyping}
                className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-md hover:shadow-lg"
              >
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AITeacherChatbot;
